import {
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import {
  CheckCircle2,
  XCircle,
  FileQuestion,
  AlertTriangle,
  FileCode2,
  RefreshCw,
  ExternalLink,
  User,
  Shield,
  Target,
  ChevronDown,
  ChevronRight,
  Link2,
} from "lucide-react";
import { useState } from "react";
import { FaGithub } from "react-icons/fa";

import type { RouterOutputs } from "@/infrastructure/trpc/router";

import { cn } from "@/infrastructure/lib/utils";
import { useTRPC } from "@/infrastructure/trpc/react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
export const Route = createFileRoute(
  "/app/organization/$id/settings/github/config"
)({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient
        .query(
          context.trpc.githubApp.getInstallation.queryOptions(
            {
              organizationId: params.id,
            }
          )
        )
        .catch(() => undefined),
      context.queryClient
        .query(
          context.trpc.githubApp.getVivusConfigStatus.queryOptions(
            {
              organizationId: params.id,
            }
          )
        )
        .catch(() => undefined),
    ]);
  },
  component: RouteComponent,
});

// Derive types from TRPC router outputs
type GetVivusConfigStatusResult =
  RouterOutputs["githubApp"]["getVivusConfigStatus"];
type RepoConfig =
  GetVivusConfigStatusResult["repositories"][number];
type ConfigStatus = RepoConfig["configStatus"];

/**
 * Sort repositories:
 * 1. By config status priority (valid > invalid > error > not_found)
 * 2. Alphabetically by fullName within each group
 */
function sortRepositories(
  repositories: RepoConfig[]
): RepoConfig[] {
  const statusPriority: Record<
    ConfigStatus["status"],
    number
  > = {
    valid: 0,
    invalid: 1,
    error: 2,
    not_found: 3,
  };

  return [...repositories].sort((a, b) => {
    const priorityA = statusPriority[a.configStatus.status];
    const priorityB = statusPriority[b.configStatus.status];

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.fullName.localeCompare(b.fullName);
  });
}

// Extract VivusRule type from valid config status
type ValidConfigStatus = Extract<
  ConfigStatus,
  { status: "valid" }
>;
type VivusRule = ValidConfigStatus["rules"][number];

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();

  const { data: installation } = useSuspenseQuery(
    trpc.githubApp.getInstallation.queryOptions({
      organizationId: id,
    })
  );

  const { data, isLoading, isFetching, refetch } = useQuery(
    {
      ...trpc.githubApp.getVivusConfigStatus.queryOptions({
        organizationId: id,
      }),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  if (!installation) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="mb-1 text-lg font-semibold">
            vivus.json Configuration
          </h3>
          <p className="text-sm text-muted-foreground">
            Repository configuration files for alert
            suppression and routing
          </p>
        </div>

        <Card>
          <CardContent
            variant="py12"
            className="text-center"
          >
            <FileCode2 className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
            <h3 className="mb-2 font-medium">
              GitHub App Not Connected
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Connect the GitHub App first to view
              configuration status.
            </p>
            <Link
              to="/app/organization/$id/settings/github"
              params={{ id }}
            >
              <Button>Connect GitHub App</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="mb-1 text-lg font-semibold">
            vivus.json Configuration
          </h3>
          <p className="text-sm text-muted-foreground">
            Repository configuration files for alert
            suppression and routing
          </p>
        </div>
        <Card>
          <CardContent variant="py6">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data?.hasGitHubApp) {
    return null;
  }

  const validCount = data.repositories.filter(
    (r) => r.configStatus.status === "valid"
  ).length;
  const invalidCount = data.repositories.filter(
    (r) => r.configStatus.status === "invalid"
  ).length;
  const notFoundCount = data.repositories.filter(
    (r) => r.configStatus.status === "not_found"
  ).length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-lg font-semibold">
          vivus.json Configuration
        </h3>
        <p className="text-sm text-muted-foreground">
          Repository configuration files for alert
          suppression and routing. Add a `vivus.json` file
          to your repository root to customize behavior.
        </p>
      </div>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileCode2 className="h-5 w-5" />
              <CardTitle variant="base">
                Repository Status
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {validCount > 0 && (
                  <Badge variant="successSoft" className="">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {validCount}
                  </Badge>
                )}
                {invalidCount > 0 && (
                  <Badge variant="errorSoft">
                    <XCircle className="mr-1 h-3 w-3" />
                    {invalidCount}
                  </Badge>
                )}
                {notFoundCount > 0 && (
                  <Badge variant="neutralSoft">
                    <FileQuestion className="mr-1 h-3 w-3" />
                    {notFoundCount}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  void refetch();
                }}
                disabled={isFetching}
              >
                <RefreshCw
                  className={cn(
                    "h-4 w-4",
                    isFetching && "animate-spin"
                  )}
                />
              </Button>
            </div>
          </div>
          <CardDescription>
            {data.repositories.length} repositories with
            GitHub App access
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.repositories.length === 0 ? (
            <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
              <FaGithub className="h-5 w-5" />
              <span>
                No repositories found. Make sure the GitHub
                App has access to at least one repository.
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {sortRepositories(data.repositories).map(
                (repo) => (
                  <RepoConfigRow
                    key={repo.fullName}
                    fullName={repo.fullName}
                    configStatus={repo.configStatus}
                  />
                )
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documentation Card */}
      <Card>
        <CardHeader variant="compact">
          <CardTitle variant="base">
            Configuration Format
          </CardTitle>
          <CardDescription>
            Example vivus.json file structure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="overflow-x-auto rounded-lg bg-muted/50 p-4 text-xs">
            {JSON.stringify(
              {
                $schema:
                  "https://vivus.blogic.cz/schemas/vivus-config/v1/json",
                version: "1",
                defaultAssignee: "octocat",
                targets: [
                  {
                    namespace: "my-app-*",
                    cluster: "prod",
                  },
                ],
                rules: [
                  {
                    target: { namespace: "staging-*" },
                    suppress: {
                      ImagePullBackOff: {
                        reason: "Staging uses local images",
                      },
                    },
                  },
                ],
              },
              null,
              2
            )}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

type RepoConfigRowProps = {
  fullName: string;
  configStatus: ConfigStatus;
};

function RepoConfigRow({
  fullName,
  configStatus,
}: RepoConfigRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasRules =
    configStatus.status === "valid" &&
    configStatus.rules.length > 0;
  const hasTargets =
    configStatus.status === "valid" &&
    configStatus.targets.length > 0;
  const hasRepoMappings =
    configStatus.status === "valid" &&
    configStatus.repoMappingsCount > 0;
  const canExpand =
    hasRules || hasTargets || hasRepoMappings;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border transition-colors",
        configStatus.status === "valid" &&
          "border-success-border/20 bg-success/5",
        configStatus.status === "invalid" &&
          "border-destructive/20 bg-destructive/5",
        configStatus.status === "not_found" &&
          "border-border/20 bg-neutral/5",
        configStatus.status === "error" &&
          "border-warning-border/20 bg-warning/5"
      )}
    >
      {/* Header row */}
      <div
        className={cn(
          "group relative flex items-center justify-between gap-4 p-3 transition-colors",
          canExpand && "cursor-pointer",
          configStatus.status === "valid" &&
            "hover:bg-success/10",
          configStatus.status === "invalid" &&
            "hover:bg-destructive/10",
          configStatus.status === "not_found" &&
            "hover:bg-neutral/10",
          configStatus.status === "error" &&
            "hover:bg-warning/10"
        )}
        onClick={() => {
          if (canExpand) setIsExpanded(!isExpanded);
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          {canExpand ? (
            <button
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:bg-success/20"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-success-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-success-foreground" />
              )}
            </button>
          ) : (
            <StatusIcon status={configStatus.status} />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium">
                {fullName}
              </span>
              <a
                href={`https://github.com/${fullName}`}
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-0 transition-opacity group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </a>
            </div>
            <StatusDescription
              configStatus={configStatus}
            />
          </div>
        </div>

        {configStatus.status === "valid" && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {configStatus.rulesCount > 0 && (
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" />
                    <span>{configStatus.rulesCount}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {configStatus.rulesCount} suppression rule
                  {configStatus.rulesCount !== 1 ? "s" : ""}
                </TooltipContent>
              </Tooltip>
            )}
            {configStatus.targetsCount > 0 && (
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1">
                    <Target className="h-3.5 w-3.5" />
                    <span>{configStatus.targetsCount}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {configStatus.targetsCount} target
                  declaration
                  {configStatus.targetsCount !== 1
                    ? "s"
                    : ""}
                </TooltipContent>
              </Tooltip>
            )}
            {hasRepoMappings && (
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1">
                    <Link2 className="h-3.5 w-3.5" />
                    <span>
                      {configStatus.repoMappingsCount}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {configStatus.repoMappingsCount} repo
                  mapping
                  {configStatus.repoMappingsCount !== 1
                    ? "s"
                    : ""}
                </TooltipContent>
              </Tooltip>
            )}
            {configStatus.defaultAssignee && (
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    <span className="max-w-25 truncate">
                      @{configStatus.defaultAssignee}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  Default assignee: @
                  {configStatus.defaultAssignee}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {configStatus.status === "not_found" && (
          <a
            href={`https://github.com/${fullName}/new/main?filename=vivus.json&value=${encodeURIComponent(
              JSON.stringify(
                {
                  $schema:
                    "https://vivus.blogic.cz/schemas/vivus-config/v1/json",
                  version: "1",
                  rules: [],
                },
                null,
                2
              )
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            Create file
          </a>
        )}
      </div>

      {/* Expandable rules section */}
      {isExpanded && configStatus.status === "valid" && (
        <div className="space-y-4 border-t border-success-border/20 bg-success bg-success/60 px-4 py-3">
          {/* Suppression Rules */}
          {configStatus.rules.length > 0 && (
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wider text-success-foreground uppercase">
                <Shield className="h-3.5 w-3.5" />
                Suppression Rules
              </h4>
              <div className="space-y-2">
                {configStatus.rules.map((rule, idx) => (
                  <RuleCard key={idx} rule={rule} />
                ))}
              </div>
            </div>
          )}

          {/* Target Declarations */}
          {configStatus.targets.length > 0 && (
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wider text-success-foreground uppercase">
                <Target className="h-3.5 w-3.5" />
                Target Declarations
              </h4>
              <div className="flex flex-wrap gap-2">
                {configStatus.targets.map((target, idx) => (
                  <div
                    key={idx}
                    className="inline-flex items-center gap-1.5 rounded border border-success-border/20 bg-success/15 bg-success/20 px-2 py-1 text-xs text-success-foreground"
                  >
                    <span>{target.namespace}</span>
                    <span className="text-success-foreground">
                      @
                    </span>
                    <span>{target.cluster}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Repo Mappings */}
          {configStatus.repoMappings.length > 0 && (
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wider text-success-foreground uppercase">
                <Link2 className="h-3.5 w-3.5" />
                Repo Mappings
              </h4>
              <div className="flex flex-wrap gap-2">
                {configStatus.repoMappings.map(
                  (mapping) => (
                    <div
                      key={`${mapping.namespace}/${mapping.workload}`}
                      className="inline-flex items-center gap-1.5 rounded border border-success-border/20 bg-success/15 bg-success/20 px-2 py-1 text-xs text-success-foreground"
                    >
                      <span>{mapping.workload}</span>
                      <span className="text-success-foreground">
                        @
                      </span>
                      <span>{mapping.namespace}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RuleCard({ rule }: { rule: VivusRule }) {
  const suppressedAlerts = Object.entries(
    rule.suppress
  ).filter(([, v]) => v !== undefined);

  return (
    <div className="rounded-md border border-success-border/20 bg-success/10 bg-success/30 p-3 text-xs">
      {/* Target */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">
          Target:
        </span>
        {rule.target.namespace && (
          <Badge
            size="compact"
            variant="outline"
            className="h-5"
          >
            ns: {rule.target.namespace}
          </Badge>
        )}
        {rule.target.cluster && (
          <Badge
            size="compact"
            variant="outline"
            className="h-5"
          >
            cluster: {rule.target.cluster}
          </Badge>
        )}
        {rule.target.controller && (
          <Badge
            size="compact"
            variant="outline"
            className="h-5"
          >
            controller: {rule.target.controller}
          </Badge>
        )}
      </div>

      {/* Suppressed alerts */}
      <div className="space-y-1">
        {suppressedAlerts.map(
          ([alertType, suppression]) => (
            <div
              key={alertType}
              className="flex items-start gap-2 text-success-foreground"
            >
              <Shield className="mt-0.5 h-3 w-3 flex-shrink-0 text-success-foreground" />
              <div>
                <span className="font-medium">
                  {alertType}
                </span>
                <span className="ml-1.5 text-muted-foreground">
                  — {suppression?.reason}
                </span>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function StatusIcon({
  status,
}: {
  status: "valid" | "invalid" | "not_found" | "error";
}) {
  switch (status) {
    case "valid":
      return (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-success/20">
          <CheckCircle2 className="h-4 w-4 text-success-foreground" />
        </div>
      );
    case "invalid":
      return (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-destructive/20">
          <XCircle className="h-4 w-4 text-destructive" />
        </div>
      );
    case "not_found":
      return (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral/20">
          <FileQuestion className="h-4 w-4 text-neutral-foreground" />
        </div>
      );
    case "error":
      return (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-warning/20">
          <AlertTriangle className="h-4 w-4 text-warning-foreground" />
        </div>
      );
  }
}

function StatusDescription({
  configStatus,
}: {
  configStatus: ConfigStatus;
}) {
  switch (configStatus.status) {
    case "valid":
      return (
        <p className="text-xs text-success-foreground/80">
          Valid configuration
        </p>
      );
    case "invalid":
      return (
        <p className="max-w-75 truncate text-xs text-destructive/80">
          {configStatus.error}
        </p>
      );
    case "not_found":
      return (
        <p className="text-xs text-neutral-foreground">
          No vivus.json file
        </p>
      );
    case "error":
      return (
        <p className="max-w-75 truncate text-xs text-warning-foreground/80">
          {configStatus.error}
        </p>
      );
  }
}
